import React, { useEffect, useState } from 'react';
import { X, Smartphone, Phone, Star, Navigation, Loader2 } from 'lucide-react';
import { Input } from '../ui/Input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/Select';
import { PercentSlider } from './BookingFormControls';
import commandCenterService from '../../services/commandCenterService';

// Same 4-type allowlist (and real AmbulanceTypes names) the booking form's
// Vehicle Type checklist uses — this used to be a hardcoded ['ALS', 'BLS'],
// neither of which matches any real ambulance type name. The backend's
// assignManualDriver does a fuzzy `contains` match against real type names,
// so "BLS" was silently matching an unrelated type ("Basic Life Support
// (BLS)", not one of Command Center's 4), and "ALS" matched nothing at all
// and fell back to whatever type was alphabetically first system-wide —
// assigning a type the dispatcher never selected either way.
const COMMAND_CENTER_VEHICLE_TYPES = [
  'Advanced Life Support',
  'Advanced Cardiac Life Support',
  'Basic Life Support Small',
  'Basic Life Support Big',
];

// Reasons a dispatcher can give for calling a driver instead of using the app
// they're known to have — required so bypassing the app leaves an audit trail.
const CALL_REASONS = [
  'Driver not responding on app',
  'Urgent — cannot wait',
  'Driver requested call',
  'App issue',
];

const FieldLabel = ({ children }) => (
  <label className="text-xs font-semibold text-gray-500 mb-1.5 block">{children}</label>
);

// Shared "confirm price + notify" popup for both assigning a driver already in
// the nearby list (mode="driver", always has the app for now) and Add & Assign
// — a driver not in the mock pool (mode="manual", no app by definition). Ends
// either by "Assign via App" (mode="driver" only) or "Assign by Call" (always
// available; requires a reason when bypassing an available app), which
// finalizes the assignment in the parent.
const CommandCenterAssignPricingPopup = ({ mode, driver, priceFields, assigning = false, onClose, onAssign, onError }) => {
  const hasApp = mode === 'driver';
  const [prices, setPrices] = useState({ ...priceFields });
  const [partnerShare, setPartnerShare] = useState(50);
  const [manualDriver, setManualDriver] = useState({
    name: '',
    phone: '',
    vehicleType: '',
    vehiclePlate: '',
    vendorName: '',
  });
  const [showCallReason, setShowCallReason] = useState(false);
  const [callReason, setCallReason] = useState('');
  const [vehicleTypeOptions, setVehicleTypeOptions] = useState([]);
  // Add & Assign (mode="manual") only — a last confirmation step before the
  // actual assignment fires, since submitting here also registers a brand
  // new ambulance/driver record, not just a dispatch action.
  const [showAssignConfirm, setShowAssignConfirm] = useState(false);

  useEffect(() => {
    if (mode !== 'manual') return undefined;
    let cancelled = false;
    commandCenterService.getAmbulanceTypes()
      .then((result) => {
        if (cancelled) return;
        const allTypes = Array.isArray(result.data) ? result.data : [];
        const filtered = COMMAND_CENTER_VEHICLE_TYPES
          .map((name) => allTypes.find((t) => t.name === name))
          .filter(Boolean);
        setVehicleTypeOptions(filtered);
      })
      .catch((error) => {
        if (!cancelled) onError?.(error.message || 'Failed to load ambulance types');
      });
    return () => { cancelled = true; };
  }, [mode, onError]);

  const updatePrice = (key, value) => setPrices((prev) => ({ ...prev, [key]: value }));
  const updateManual = (key, value) => setManualDriver((prev) => ({ ...prev, [key]: value }));

  const canAssign = (mode === 'driver' || (manualDriver.name.trim() && manualDriver.phone.trim())) && !assigning;

  const buildDriver = () => {
    if (mode === 'driver') return driver;
    return {
      id: `manual-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      name: manualDriver.name.trim(),
      phone: manualDriver.phone.trim(),
      vehiclePlate: manualDriver.vehiclePlate.trim() || '—',
      vehicleType: manualDriver.vehicleType || null,
      vehicleTypeLabel: manualDriver.vehicleType || 'Not specified',
      vendorName: manualDriver.vendorName.trim() || 'Independent Driver',
      rating: null,
      estimatedFare: null,
      location: null,
      distanceKm: null,
      etaMinutes: null,
      isManual: true,
    };
  };

  const handleAssign = (notifyMethod, reason = undefined) => {
    if (!canAssign) return;
    // undefined (not null) so JSON.stringify omits the key entirely when
    // there's no reason (e.g. "Assign via App") — the backend's Joi schema
    // only allows the field to be absent, not explicitly null.
    onAssign({ driver: buildDriver(), prices, notifyMethod, callReason: reason });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[92vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between shrink-0">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">
              {mode === 'driver' ? 'Confirm Assignment' : showAssignConfirm ? 'Confirm Assignment' : 'Add & Assign'}
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              {mode === 'driver'
                ? 'Confirm the driver, then notify them.'
                : showAssignConfirm
                  ? 'Double-check the details before this registers the ambulance and assigns it.'
                  : 'Set the trip price, then notify the driver.'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-7 h-7 flex items-center justify-center rounded-md border border-gray-200 text-gray-500 hover:bg-gray-50"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto px-5 py-3 space-y-3 text-sm">
          {mode === 'driver' && driver && (
            <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
              <div className="font-medium text-gray-900">{driver.name}</div>
              <div className="text-xs text-gray-500 mt-0.5">{driver.vendorName} &middot; {driver.vehicleTypeLabel} &middot; {driver.vehiclePlate}</div>
              <div className="flex items-center gap-3 text-xs text-gray-500 mt-1.5">
                {driver.phone && (
                  <a
                    href={`tel:${driver.phone}`}
                    className="inline-flex items-center gap-1 text-blue-600 font-medium hover:underline cursor-pointer"
                    title={`Call driver: ${driver.phone}`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Phone className="w-3 h-3" />
                    <span>{driver.phone}</span>
                  </a>
                )}
                {driver.rating && (
                  <span className="inline-flex items-center gap-1 text-amber-500">
                    <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                    {driver.rating}
                  </span>
                )}
                {driver.distanceKm != null && (
                  <span className="inline-flex items-center gap-1">
                    <Navigation className="w-3 h-3" />
                    {driver.distanceKm.toFixed(1)} km
                  </span>
                )}
              </div>
            </div>
          )}

          {mode === 'manual' && showAssignConfirm && (
            <div className="space-y-3">
              <p className="text-sm text-gray-700">
                Are you sure you want to assign <span className="font-semibold text-gray-900">{manualDriver.name.trim()}</span>?
              </p>
              <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                <div className="font-medium text-gray-900">{manualDriver.name.trim()}</div>
                <div className="text-xs text-gray-500 mt-0.5">
                  {manualDriver.vendorName.trim() || 'Independent Driver'} &middot; {manualDriver.vehicleType || 'Not specified'} &middot; {manualDriver.vehiclePlate.trim() || '—'}
                </div>
                {manualDriver.phone.trim() && (
                  <a
                    href={`tel:${manualDriver.phone.trim()}`}
                    className="inline-flex items-center gap-1 text-blue-600 font-medium hover:underline cursor-pointer"
                    title={`Call driver: ${manualDriver.phone.trim()}`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Phone className="w-3 h-3" />
                    <span>{manualDriver.phone.trim()}</span>
                  </a>
                )}
              </div>
              <p className="text-xs text-gray-500">
                This will also send an ambulance registration request to Admin. Click Edit to go back and change any details.
              </p>
            </div>
          )}

          {mode === 'manual' && !showAssignConfirm && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <p className="sm:col-span-2 text-xs text-gray-500">
                Enter the basic details to assign this ambulance now — submitting also sends an ambulance
                registration request to Admin, who will complete the full registration on their side.
              </p>
              <div>
                <FieldLabel>Driver Name <span className="text-red-500">*</span></FieldLabel>
                <Input
                  value={manualDriver.name}
                  onChange={(e) => updateManual('name', e.target.value)}
                  placeholder="Driver name"
                />
              </div>
              <div>
                <FieldLabel>Mobile No <span className="text-red-500">*</span></FieldLabel>
                <Input
                  value={manualDriver.phone}
                  onChange={(e) => updateManual('phone', e.target.value.replace(/\D/g, '').slice(0, 10))}
                  placeholder="Contact number"
                />
              </div>
              <div>
                <FieldLabel>Ambulance Type</FieldLabel>
                <Select value={manualDriver.vehicleType} onValueChange={(v) => updateManual('vehicleType', v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select type..." />
                  </SelectTrigger>
                  <SelectContent>
                    {vehicleTypeOptions.map((type) => (
                      <SelectItem key={type.type_id} value={type.name}>
                        <span title={type.name} className="cursor-help">
                          {type.abbreviation || type.name}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <FieldLabel>Vehicle No</FieldLabel>
                <Input
                  value={manualDriver.vehiclePlate}
                  onChange={(e) => updateManual('vehiclePlate', e.target.value)}
                  placeholder="e.g. GJ01AB1234"
                />
              </div>
              <div className="sm:col-span-2">
                <FieldLabel>Company Name</FieldLabel>
                <Input
                  value={manualDriver.vendorName}
                  onChange={(e) => updateManual('vendorName', e.target.value)}
                  placeholder="Fleet / vendor name"
                />
              </div>
            </div>
          )}

          {mode === 'manual' && !showAssignConfirm && (
            <>
              <div>
                <FieldLabel>Pricing</FieldLabel>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {/* System Price commented out per request
                  <div>
                    <label className="text-xs text-gray-400 mb-1 block">System Price</label>
                    <Input type="number" min="0" step="any" value={prices.systemPrice} disabled placeholder="Auto-calculated" />
                  </div>
                  */}
                  <div>
                    <label className="text-xs text-gray-400 mb-1 block">Fleet Operator Price</label>
                    <Input
                      type="number"
                      min="0"
                      step="any"
                      value={prices.fleetOperatorPrice ?? prices.offeredPrice ?? ''}
                      onChange={(e) => {
                        const val = e.target.value;
                        const fo = val !== '' ? Number(val) : 0;
                        const ps = Number(prices.platformServiceFee ?? prices.agreedPrice ?? 0);
                        const fos = Number(prices.fleetOperatorServiceFee ?? prices.partnerPrice ?? 0);
                        const sum = fo + ps + fos;
                        setPrices((prev) => ({
                          ...prev,
                          fleetOperatorPrice: val,
                          totalPrice: sum > 0 ? sum : '',
                        }));
                      }}
                      placeholder="₹ 0"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-400 mb-1 block">Platform Service Fee</label>
                    <Input
                      type="number"
                      min="0"
                      step="any"
                      value={prices.platformServiceFee ?? prices.agreedPrice ?? ''}
                      onChange={(e) => {
                        const val = e.target.value;
                        const ps = val !== '' ? Number(val) : 0;
                        const fo = Number(prices.fleetOperatorPrice ?? prices.offeredPrice ?? 0);
                        const fos = Number(prices.fleetOperatorServiceFee ?? prices.partnerPrice ?? 0);
                        const sum = fo + ps + fos;
                        setPrices((prev) => ({
                          ...prev,
                          platformServiceFee: val,
                          totalPrice: sum > 0 ? sum : '',
                        }));
                      }}
                      placeholder="₹ 0"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-400 mb-1 block">Operator Service Fee</label>
                    <Input
                      type="number"
                      min="0"
                      step="any"
                      value={prices.fleetOperatorServiceFee ?? prices.partnerPrice ?? ''}
                      onChange={(e) => {
                        const val = e.target.value;
                        const fos = val !== '' ? Number(val) : 0;
                        const fo = Number(prices.fleetOperatorPrice ?? prices.offeredPrice ?? 0);
                        const ps = Number(prices.platformServiceFee ?? prices.agreedPrice ?? 0);
                        const sum = fo + ps + fos;
                        setPrices((prev) => ({
                          ...prev,
                          fleetOperatorServiceFee: val,
                          totalPrice: sum > 0 ? sum : '',
                        }));
                      }}
                      placeholder="₹ 0"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="text-xs text-gray-400 mb-1 block font-semibold">Total Price</label>
                    <Input
                      type="number"
                      min="0"
                      step="any"
                      value={prices.totalPrice ?? ''}
                      onChange={(e) => updatePrice('totalPrice', e.target.value)}
                      placeholder="₹ 0"
                    />
                  </div>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <FieldLabel>Partner Share Split</FieldLabel>
                  <span className="text-sm font-semibold text-primary">{partnerShare}%</span>
                </div>
                <PercentSlider
                  value={partnerShare}
                  onChange={(v) => {
                    setPartnerShare(v);
                    if (prices.agreedPrice !== '' && prices.agreedPrice != null) {
                      updatePrice('partnerPrice', Math.round(Number(prices.agreedPrice) * v / 100));
                    }
                  }}
                />
              </div>
            </>
          )}
        </div>

        <div className="px-5 py-3 border-t border-gray-100 shrink-0">
          {hasApp ? (
            <div className="space-y-2">
              <button
                disabled={!canAssign}
                onClick={() => handleAssign('app')}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-primary hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed text-primary-foreground text-sm font-semibold transition-colors"
              >
                {assigning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Smartphone className="w-4 h-4" />}
                Assign via App
              </button>

              {!showCallReason ? (
                <button
                  onClick={() => setShowCallReason(true)}
                  className="w-full inline-flex items-center justify-center px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 border border-gray-200 text-gray-700 text-xs font-medium transition-colors"
                >
                  Assign by Call instead
                </button>
              ) : (
                <div className="pt-1 space-y-2">
                  <FieldLabel>Reason for calling instead <span className="text-red-500">*</span></FieldLabel>
                  <Select value={callReason} onValueChange={setCallReason}>
                    <SelectTrigger className="bg-white border-gray-300">
                      <SelectValue placeholder="Select a reason..." />
                    </SelectTrigger>
                    <SelectContent side="top">
                      {CALL_REASONS.map((reason) => (
                        <SelectItem key={reason} value={reason}>{reason}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="flex gap-2">
                    <button
                      onClick={() => { setShowCallReason(false); setCallReason(''); }}
                      className="flex-1 inline-flex items-center justify-center px-3 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 text-xs font-medium transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      disabled={!canAssign || !callReason}
                      onClick={() => handleAssign('call', callReason)}
                      className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-primary/30 text-primary hover:bg-primary/5 disabled:opacity-40 disabled:cursor-not-allowed text-xs font-semibold transition-colors"
                    >
                      {assigning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Phone className="w-3.5 h-3.5" />}
                      Confirm Call
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : showAssignConfirm ? (
            <div className="flex gap-2">
              <button
                onClick={() => setShowAssignConfirm(false)}
                disabled={assigning}
                className="flex-1 inline-flex items-center justify-center px-4 py-2.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed text-sm font-semibold transition-colors"
              >
                Edit
              </button>
              <button
                disabled={!canAssign}
                onClick={() => handleAssign('call')}
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-primary hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed text-primary-foreground text-sm font-semibold transition-colors"
              >
                {assigning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Phone className="w-4 h-4" />}
                Assign
              </button>
            </div>
          ) : (
            <button
              disabled={!canAssign}
              onClick={() => setShowAssignConfirm(true)}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-primary hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed text-primary-foreground text-sm font-semibold transition-colors"
            >
              <Phone className="w-4 h-4" />
              Assign by Call
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CommandCenterAssignPricingPopup;
